/**
 * Default styles for the RLHF grading elements. Every element renders into LIGHT
 * DOM (the KitElement contract), so these are plain global rules scoped by the
 * `rlhf-*` tag names — plus a handful of GLOBAL utility classes (`.rlhf-seam`,
 * `.rlhf-field`, `.rlhf-btn`, `.rlhf-vh`) a host surface reuses directly for its
 * own inputs and buttons. Everything tunable is a `--rlhf-*` custom property with
 * a fallback that points at a host token (`--brand`, `--ink`, …) so a host app's
 * token layer drives the look; nothing here hard-codes a brand.
 *
 * `installRlhfStyles(doc)` is idempotent per document (adopted stylesheet where
 * available, `<style data-rlhf>` fallback) — the same pattern as web-kit's brand
 * styles. `defineRlhf*()` helpers call it, so apps rarely need to.
 *
 * The flagship reusable primitive is »seamless input« (`.rlhf-seam`): a box-less,
 * transparent, auto-resizing textarea that indicates itself ONLY via an underline
 * — invisible at rest, subtle on hover, accent-colored on focus. The underline
 * NEVER shifts layout: a constant 2px bottom border only ever changes COLOR, and
 * the focused »thick« state is an inset box-shadow (shadows don't affect layout).
 * See `apps/career-studio/docs/rlhf-ui-patterns.md`.
 */

export const RLHF_STYLES_CSS = /* css */ `
:where(rlhf-annotated-text, rlhf-dimension-grader, rlhf-pairwise, rlhf-session-bar, rlhf-thumbs) {
  --rlhf-ink: var(--ink, #1c1a17);
  --rlhf-ink-soft: var(--muted, #6b6459);
  --rlhf-ink-faint: var(--faint, color-mix(in oklab, var(--rlhf-ink) 52%, transparent));
  --rlhf-surface: var(--surface, #fbf9f4);
  --rlhf-raised: var(--raised, color-mix(in oklab, var(--rlhf-ink) 3%, var(--rlhf-surface)));
  --rlhf-sunk: var(--sunk, color-mix(in oklab, var(--rlhf-ink) 6%, var(--rlhf-surface)));
  --rlhf-line: var(--line, color-mix(in oklab, var(--rlhf-ink) 14%, transparent));
  --rlhf-line-strong: var(--line-2, color-mix(in oklab, var(--rlhf-ink) 26%, transparent));
  --rlhf-accent: var(--brand, #bd5b34);
  --rlhf-accent-ink: var(--brand-ink, #fff);
  --rlhf-focus: var(--rlhf-accent);
  --rlhf-danger: var(--nogo, #a1341f);
  --rlhf-warn: var(--meh, #9a6b16);
  --rlhf-positive: var(--choose, #1d6b4f);
  --rlhf-radius: var(--radius-md, 0.5rem);
  --rlhf-radius-lg: var(--radius-lg, 0.65rem);
  --rlhf-gap: 0.75rem;
  display: block;
  color: var(--rlhf-ink);
  font: inherit;
}

/* ═══ Global primitives (a host surface reuses these class names) ═══════════ */

/* »Seamless input«: box-less, transparent, auto-resizing; underline-only affordance. */
.rlhf-seam {
  display: block;
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  color: inherit;
  background: transparent;
  /* Constant 2px border reserves the space PERMANENTLY — hover/focus change only
     its color, never its width, so following content can never flick. */
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 0.1rem 0 0.15rem;
  margin: 0;
  resize: none;
  /* Chrome-first auto-resize; min-height:0 means a single-line value takes ONE
     line, with no reserved empty rows. seam.ts adds a JS fallback where missing. */
  field-sizing: content;
  min-height: 0;
  line-height: 1.55;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;
}
.rlhf-seam::placeholder { color: var(--rlhf-ink-faint, #9b948a); }
.rlhf-seam:hover { border-bottom-color: var(--rlhf-line-strong, rgba(0,0,0,0.26)); }
/* The colored underline IS the focus indicator (never remove focusability). The
   thick state is an inset shadow UNDER the border — a shadow, so zero layout shift. */
.rlhf-seam:focus,
.rlhf-seam:focus-visible {
  outline: none;
  border-bottom-color: var(--rlhf-focus, var(--brand, #bd5b34));
  box-shadow: inset 0 -1px 0 0 var(--rlhf-focus, var(--brand, #bd5b34));
}
@media (prefers-reduced-motion: reduce) { .rlhf-seam { transition: none; } }

/* A labelled field: a quiet caption stacked over its control. */
.rlhf-field { display: grid; gap: 0.2rem; }
.rlhf-field-label {
  font-size: 0.76rem; font-weight: 500; letter-spacing: 0.01em;
  color: var(--rlhf-ink-soft, #6b6459);
}

/* A subtle "your unsaved text was restored from a reload" cue under a seam field. */
.rlhf-draft-hint {
  font-size: 0.72rem; font-style: italic;
  color: var(--rlhf-ink-faint, color-mix(in oklab, var(--rlhf-ink) 52%, transparent));
}
.rlhf-draft-hint::before { content: "↺ "; font-style: normal; }

/* Button hierarchy: primary (accent-filled) · default (outline) · quiet (ghost). */
.rlhf-btn {
  font: inherit; font-size: 0.85rem; font-weight: 500;
  cursor: pointer; border-radius: var(--rlhf-radius, 0.5rem);
  border: 1px solid var(--rlhf-line, rgba(0,0,0,0.14));
  background: var(--rlhf-surface, #fbf9f4); color: inherit;
  padding: 0.42rem 0.85rem;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.rlhf-btn:hover { border-color: var(--rlhf-line-strong, rgba(0,0,0,0.26)); background: var(--rlhf-raised, #f3efe7); }
.rlhf-btn:focus-visible { outline: 2px solid var(--rlhf-focus, var(--brand, #bd5b34)); outline-offset: 2px; }
.rlhf-btn[disabled] { opacity: 0.5; cursor: default; }
.rlhf-btn-primary {
  background: var(--rlhf-accent, #bd5b34); border-color: var(--rlhf-accent, #bd5b34);
  color: var(--rlhf-accent-ink, #fff);
}
.rlhf-btn-primary:hover:not([disabled]) {
  background: color-mix(in oklab, var(--rlhf-accent, #bd5b34) 88%, #000);
  border-color: color-mix(in oklab, var(--rlhf-accent, #bd5b34) 88%, #000);
}
.rlhf-btn-quiet { background: transparent; border-color: transparent; color: var(--rlhf-ink-soft, #6b6459); }
.rlhf-btn-quiet:hover { background: var(--rlhf-raised, #f3efe7); color: var(--rlhf-ink, #1c1a17); }
@media (prefers-reduced-motion: reduce) { .rlhf-btn { transition: none; } }

.rlhf-vh {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

/* ═══ rlhf-dimension-grader ═════════════════════════════════════════════════ */

rlhf-dimension-grader .rlhf-grader { display: grid; gap: 0.7rem; }
rlhf-dimension-grader .rlhf-grader-head { display: flex; align-items: baseline; gap: 0.5rem; flex-wrap: wrap; }
rlhf-dimension-grader .rlhf-grader-title {
  margin: 0; font-size: 1rem; font-weight: 550; line-height: 1.25;
  display: flex; align-items: baseline; gap: 0.45rem; flex-wrap: wrap;
}
rlhf-dimension-grader .rlhf-grader-id {
  font-variant-numeric: tabular-nums; font-size: 0.72rem; font-weight: 600;
  color: var(--rlhf-ink-faint); letter-spacing: 0.03em;
}
rlhf-dimension-grader .rlhf-grader-badge {
  margin-left: auto; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; padding: 0.15rem 0.45rem; border-radius: 0.35rem;
  color: var(--rlhf-danger);
  background: color-mix(in oklab, var(--rlhf-danger) 12%, transparent);
  border: 1px solid color-mix(in oklab, var(--rlhf-danger) 40%, transparent);
}

/* Rating scale: a real radiogroup with generous targets. */
rlhf-dimension-grader .rlhf-rating { display: grid; gap: 0.45rem; }
rlhf-dimension-grader .rlhf-scale { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.4rem; }
rlhf-dimension-grader .rlhf-scale-opt {
  display: flex; align-items: center; justify-content: center;
  min-height: 2.7rem; padding: 0.4rem;
  border: 1.5px solid var(--rlhf-line); border-radius: var(--rlhf-radius);
  background: var(--rlhf-surface); color: var(--rlhf-ink);
  font: inherit; font-variant-numeric: tabular-nums; font-size: 1.05rem; font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.08s ease;
}
rlhf-dimension-grader .rlhf-scale-opt:hover {
  border-color: var(--rlhf-accent);
  background: color-mix(in oklab, var(--rlhf-accent) 9%, var(--rlhf-surface));
}
rlhf-dimension-grader .rlhf-scale-opt:active { transform: translateY(1px); }
rlhf-dimension-grader .rlhf-scale-opt[aria-checked="true"] {
  background: var(--rlhf-accent); border-color: var(--rlhf-accent); color: var(--rlhf-accent-ink);
}
rlhf-dimension-grader .rlhf-scale-opt:focus-visible { outline: 2px solid var(--rlhf-focus); outline-offset: 2px; }

/* Anchor caption: the hovered / selected score's rubric text, revealed in place.
   Captions are stacked (position:absolute) inside a reserved-height box, so
   toggling which one shows NEVER shifts the following content. Pure CSS. */
rlhf-dimension-grader .rlhf-anchor-cap { position: relative; min-height: 2.7em; }
rlhf-dimension-grader .rlhf-anchor {
  position: absolute; inset: 0; margin: 0; display: none;
  font-size: 0.85rem; line-height: 1.4; color: var(--rlhf-ink-soft);
}
rlhf-dimension-grader .rlhf-anchor b { color: var(--rlhf-ink); font-weight: 600; font-variant-numeric: tabular-nums; }
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="1"][aria-checked="true"]) .rlhf-anchor[data-for="1"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="2"][aria-checked="true"]) .rlhf-anchor[data-for="2"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="3"][aria-checked="true"]) .rlhf-anchor[data-for="3"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="4"][aria-checked="true"]) .rlhf-anchor[data-for="4"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="5"][aria-checked="true"]) .rlhf-anchor[data-for="5"] { display: block; }
/* Hovering the scale takes over from the selected caption. */
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale:hover) .rlhf-anchor { display: none; }
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="1"]:hover) .rlhf-anchor[data-for="1"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="2"]:hover) .rlhf-anchor[data-for="2"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="3"]:hover) .rlhf-anchor[data-for="3"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="4"]:hover) .rlhf-anchor[data-for="4"],
rlhf-dimension-grader .rlhf-rating:has(.rlhf-scale-opt[data-score="5"]:hover) .rlhf-anchor[data-for="5"] { display: block; }

rlhf-dimension-grader .rlhf-grader-actions { display: flex; }

/* Judge verdicts as attributed voices — contested cases are the whole point,
   so disagreement (Δ>1 from the operator's pick) is styled LOUD. */
rlhf-dimension-grader .rlhf-judges {
  display: grid; gap: 0.45rem; margin-top: 0.3rem; padding-top: 0.7rem;
  border-top: 1px solid var(--rlhf-line);
}
rlhf-dimension-grader .rlhf-judges-head {
  margin: 0; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--rlhf-ink-faint);
}
rlhf-dimension-grader .rlhf-judge {
  display: grid; gap: 0.25rem; padding: 0.4rem 0.55rem; border-radius: var(--rlhf-radius);
}
rlhf-dimension-grader .rlhf-judge-head { display: flex; align-items: center; gap: 0.45rem; }
rlhf-dimension-grader .rlhf-judge-model { font-weight: 600; font-size: 0.82rem; }
rlhf-dimension-grader .rlhf-judge-badge {
  font-variant-numeric: tabular-nums; font-size: 0.72rem; font-weight: 600;
  padding: 0.05rem 0.4rem; border-radius: 999px;
  background: var(--rlhf-sunk); color: var(--rlhf-ink-soft);
}
rlhf-dimension-grader .rlhf-judge-delta {
  margin-left: auto; font-variant-numeric: tabular-nums; font-size: 0.7rem; font-weight: 700;
  padding: 0.05rem 0.4rem; border-radius: 999px;
  color: var(--rlhf-warn); background: color-mix(in oklab, var(--rlhf-warn) 16%, transparent);
}
rlhf-dimension-grader .rlhf-judge-quote {
  margin: 0; font-size: 0.85rem; line-height: 1.45; color: var(--rlhf-ink-soft);
  border-left: 2px solid var(--rlhf-line); padding-left: 0.55rem;
}
rlhf-dimension-grader .rlhf-judge.is-disagree {
  background: color-mix(in oklab, var(--rlhf-warn) 10%, transparent);
  box-shadow: inset 3px 0 0 0 var(--rlhf-warn);
}
rlhf-dimension-grader .rlhf-judge.is-disagree .rlhf-judge-badge {
  color: var(--rlhf-warn); background: color-mix(in oklab, var(--rlhf-warn) 20%, transparent);
}
@media (prefers-reduced-motion: reduce) {
  rlhf-dimension-grader .rlhf-scale-opt { transition: none; }
}

/* ═══ rlhf-annotated-text ═══════════════════════════════════════════════════ */

rlhf-annotated-text .rlhf-annotated { position: relative; display: grid; gap: 0.6rem; }
rlhf-annotated-text .rlhf-para { margin: 0 0 var(--rlhf-gap); line-height: 1.6; }
rlhf-annotated-text .rlhf-mark {
  background: color-mix(in oklab, var(--rlhf-accent) 16%, transparent);
  border-bottom: 2px solid var(--rlhf-accent);
  border-radius: 2px; padding: 0 1px; cursor: default;
}
rlhf-annotated-text .rlhf-mark.is-blocker,
rlhf-annotated-text .rlhf-mark.is-major { --rlhf-accent: var(--rlhf-danger); }
rlhf-annotated-text .rlhf-mark.is-minor,
rlhf-annotated-text .rlhf-mark.is-nit { --rlhf-accent: var(--rlhf-warn); }
rlhf-annotated-text .rlhf-mark.is-positive { --rlhf-accent: var(--rlhf-positive); }

/* The annotate affordance floats near the selection (positioned by the element);
   the button row is a fallback. */
rlhf-annotated-text .rlhf-pop {
  position: absolute; z-index: 20; transform: translate(-50%, 0.45rem);
  display: inline-flex; align-items: center; gap: 0.3rem; white-space: nowrap;
  padding: 0.32rem 0.7rem; border: 0; border-radius: 999px; cursor: pointer;
  font: inherit; font-size: 0.78rem; font-weight: 550;
  background: var(--rlhf-ink); color: var(--rlhf-surface);
  box-shadow: 0 4px 16px -4px color-mix(in oklab, var(--rlhf-ink) 45%, transparent);
  animation: rlhf-pop-in 0.12s ease-out;
}
rlhf-annotated-text .rlhf-pop::before {
  content: ""; position: absolute; top: -0.28rem; left: 50%; transform: translateX(-50%);
  border: 0.3rem solid transparent; border-bottom-color: var(--rlhf-ink); border-top: 0;
}
rlhf-annotated-text .rlhf-pop:focus-visible { outline: 2px solid var(--rlhf-focus); outline-offset: 2px; }
@keyframes rlhf-pop-in { from { opacity: 0; transform: translate(-50%, 0.15rem); } to { opacity: 1; transform: translate(-50%, 0.45rem); } }
@media (prefers-reduced-motion: reduce) { rlhf-annotated-text .rlhf-pop { animation: none; } }

rlhf-annotated-text .rlhf-affordances { display: flex; align-items: center; gap: 0.6rem; }
rlhf-annotated-text .rlhf-hint { font-size: 0.8rem; color: var(--rlhf-warn); }

rlhf-annotated-text .rlhf-form {
  display: grid; gap: 0.65rem; padding: var(--rlhf-gap); margin-top: 0.2rem;
  border: 1px solid var(--rlhf-line); border-left: 3px solid var(--rlhf-accent);
  border-radius: var(--rlhf-radius); background: var(--rlhf-raised);
}
rlhf-annotated-text .rlhf-form-quote {
  margin: 0; font-style: italic; color: var(--rlhf-ink-soft);
  border-left: 2px solid var(--rlhf-line); padding-left: 0.55rem;
}
rlhf-annotated-text .rlhf-form-row { display: flex; flex-wrap: wrap; gap: 0.75rem; }
rlhf-annotated-text .rlhf-form-row .rlhf-field { flex: 1 1 8rem; }
rlhf-annotated-text select {
  font: inherit; color: inherit; background: var(--rlhf-surface);
  border: 1px solid var(--rlhf-line); border-radius: var(--rlhf-radius); padding: 0.32rem 0.5rem;
}
rlhf-annotated-text select:focus-visible { outline: 2px solid var(--rlhf-focus); outline-offset: 1px; }
rlhf-annotated-text .rlhf-form-actions { display: flex; gap: 0.5rem; }

rlhf-annotated-text .rlhf-gutter { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.4rem; }
rlhf-annotated-text .rlhf-chip {
  display: flex; gap: 0.5rem; align-items: baseline;
  border: 1px solid var(--rlhf-line); border-radius: var(--rlhf-radius);
  padding: 0.4rem 0.6rem; background: var(--rlhf-raised); font-size: 0.85rem;
}
rlhf-annotated-text .rlhf-chip-kind { font-weight: 600; }
rlhf-annotated-text .rlhf-chip-sev {
  font-size: 0.72rem; padding: 0.05rem 0.4rem; border-radius: 999px;
  background: var(--rlhf-sunk); color: var(--rlhf-ink-soft); white-space: nowrap;
}
rlhf-annotated-text .rlhf-chip-comment { color: var(--rlhf-ink-soft); }

/* ═══ rlhf-pairwise ═════════════════════════════════════════════════════════ */

rlhf-pairwise .rlhf-pairwise { display: grid; gap: 0.8rem; }
rlhf-pairwise .rlhf-vs {
  display: grid; grid-template-columns: 1fr auto 1fr; gap: var(--rlhf-gap); align-items: stretch;
}
rlhf-pairwise .rlhf-col {
  display: grid; gap: 0.5rem; align-content: start; text-align: left;
  padding: var(--rlhf-gap); font: inherit; color: inherit; cursor: pointer;
  border: 1.5px solid var(--rlhf-line); border-radius: var(--rlhf-radius-lg);
  background: var(--rlhf-surface);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
rlhf-pairwise .rlhf-col:hover { border-color: color-mix(in oklab, var(--rlhf-accent) 55%, var(--rlhf-line)); }
rlhf-pairwise .rlhf-col:focus-visible { outline: 2px solid var(--rlhf-focus); outline-offset: 2px; }
rlhf-pairwise .rlhf-col.is-winner {
  border-color: var(--rlhf-accent); box-shadow: inset 0 0 0 1.5px var(--rlhf-accent);
  background: color-mix(in oklab, var(--rlhf-accent) 6%, var(--rlhf-surface));
}
rlhf-pairwise .rlhf-col-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
rlhf-pairwise .rlhf-col-ref { font-weight: 600; font-size: 0.82rem; }
rlhf-pairwise .rlhf-col-pick {
  font-size: 0.72rem; color: var(--rlhf-ink-faint); white-space: nowrap;
}
rlhf-pairwise .rlhf-col.is-winner .rlhf-col-pick { color: var(--rlhf-accent); font-weight: 600; }
rlhf-pairwise .rlhf-col-body { display: grid; gap: 0.4rem; }
rlhf-pairwise .rlhf-col-para { margin: 0; line-height: 1.55; }
rlhf-pairwise .rlhf-tie {
  align-self: center; font: inherit; font-size: 0.78rem; font-weight: 550; cursor: pointer;
  writing-mode: vertical-rl; text-orientation: mixed; padding: 0.6rem 0.35rem;
  border: 1px solid var(--rlhf-line); border-radius: 999px;
  background: var(--rlhf-surface); color: var(--rlhf-ink-soft);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
rlhf-pairwise .rlhf-tie:hover { border-color: var(--rlhf-line-strong); color: var(--rlhf-ink); }
rlhf-pairwise .rlhf-tie:focus-visible { outline: 2px solid var(--rlhf-focus); outline-offset: 2px; }
rlhf-pairwise .rlhf-tie.is-winner {
  border-color: var(--rlhf-ink-soft); color: var(--rlhf-ink);
  background: var(--rlhf-sunk); font-weight: 600;
}
rlhf-pairwise .rlhf-pairwise-actions { display: flex; }
@media (max-width: 46rem) {
  rlhf-pairwise .rlhf-vs { grid-template-columns: 1fr; }
  rlhf-pairwise .rlhf-tie { writing-mode: horizontal-tb; padding: 0.4rem 0.7rem; justify-self: center; }
}
@media (prefers-reduced-motion: reduce) {
  rlhf-pairwise .rlhf-col, rlhf-pairwise .rlhf-tie { transition: none; }
}

/* ═══ rlhf-thumbs ═══════════════════════════════════════════════════════════

   Barely notable, and NEVER unreachable. The base state is present-but-quiet;
   hiding at rest is an ENHANCEMENT applied only where a pointer can hover, so a
   touch device (or any engine that fails the query) keeps a permanent control
   rather than inheriting an invisible one.

   Reveal contract: place <rlhf-thumbs> as a DIRECT CHILD of the element being
   judged — ":hover > rlhf-thumbs" is the reveal. A descendant selector would
   match every hovered ancestor up to <body> (always hovered while the cursor is
   in the window) and the control would never actually hide.

   Hidden here means opacity ONLY: the buttons stay in the layout, the tab order
   and the a11y tree, so keyboard and screen-reader users are unaffected and
   focus reveals them. A GIVEN signal always shows — a judgement is state, not
   chrome. Nothing here reserves or releases space, so revealing never reflows. */

:where(rlhf-thumbs) { display: inline-flex; vertical-align: middle; }

rlhf-thumbs .rlhf-thumbs {
  display: inline-flex; align-items: center; gap: 0.05rem;
  opacity: 0.45;
  transition: opacity 0.14s ease;
}
rlhf-thumbs .rlhf-thumb {
  display: inline-grid; place-items: center;
  inline-size: 1.55rem; block-size: 1.55rem; padding: 0;
  border: 0; border-radius: var(--rlhf-radius, 0.5rem);
  background: transparent; color: var(--rlhf-ink-soft, #6b6459);
  font: inherit; cursor: pointer;
  transition: color 0.14s ease, background 0.14s ease;
}
rlhf-thumbs .rlhf-thumb svg {
  inline-size: 0.95rem; block-size: 0.95rem;
  fill: none; stroke: currentColor; stroke-width: 1.6;
  stroke-linejoin: round; stroke-linecap: round;
}
/* One glyph, rotated — a thumbs-down is a thumbs-up upside down. */
rlhf-thumbs .rlhf-thumb svg.is-flipped { transform: rotate(180deg); }
rlhf-thumbs .rlhf-thumb:hover { background: var(--rlhf-raised, #f3efe7); color: var(--rlhf-ink, #1c1a17); }
rlhf-thumbs .rlhf-thumb:focus-visible { outline: 2px solid var(--rlhf-focus, var(--brand, #bd5b34)); outline-offset: 1px; }
rlhf-thumbs .rlhf-thumb.is-active.is-up { color: var(--rlhf-positive, #1d6b4f); }
rlhf-thumbs .rlhf-thumb.is-active.is-down { color: var(--rlhf-danger, #a1341f); }
rlhf-thumbs .rlhf-thumb.is-active svg { fill: color-mix(in oklab, currentColor 20%, transparent); }

/* The enhancement: on a hover-capable, fine pointer the control is invisible at
   rest and appears on the judged element's hover, on focus, or once it is set. */
@media (hover: hover) and (pointer: fine) {
  rlhf-thumbs .rlhf-thumbs { opacity: 0; }
  :hover > rlhf-thumbs .rlhf-thumbs { opacity: 0.45; }
  rlhf-thumbs:hover .rlhf-thumbs,
  rlhf-thumbs:focus-within .rlhf-thumbs,
  rlhf-thumbs .rlhf-thumbs.is-set { opacity: 1; }
}
/* No hover to reveal with: permanent, quiet, and a real 44px touch target. A touch
   user pays for the reveal they cannot use, so the resting emphasis is raised —
   still recessive, but discoverable without a gesture that does not exist here. */
@media (hover: none), (pointer: coarse) {
  rlhf-thumbs .rlhf-thumbs { opacity: 0.62; }
  rlhf-thumbs .rlhf-thumb { inline-size: 2.75rem; block-size: 2.75rem; }
  rlhf-thumbs .rlhf-thumb svg { inline-size: 1.15rem; block-size: 1.15rem; }
}
rlhf-thumbs .rlhf-thumbs.is-set { opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  rlhf-thumbs .rlhf-thumbs, rlhf-thumbs .rlhf-thumb { transition: none; }
}

/* ═══ rlhf-session-bar ══════════════════════════════════════════════════════ */

/* The host is display:contents so .rlhf-bar becomes a direct flow child of the
   consuming surface. Its position:sticky (below) then measures travel against that
   TALL container instead of the shrink-wrapped ~55px host box — which was its own
   containing block and gave the bar ZERO sticky travel (it scrolled away at once).
   The host carries no role or focus (role="group" lives on .rlhf-bar), so removing
   its box is a11y-neutral. Kept in :where() (zero specificity, overriding the earlier
   :where(... rlhf-session-bar) display:block by source order) so a host can still
   re-assert a host display with a single class if ever needed. */
:where(rlhf-session-bar) { display: contents; }

rlhf-session-bar .rlhf-bar {
  position: sticky; top: 0.5rem; z-index: 6;
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.55rem 0.9rem;
  padding: 0.55rem 0.8rem; border: 1px solid var(--rlhf-line);
  border-radius: var(--rlhf-radius-lg);
  background: color-mix(in oklab, var(--rlhf-surface) 86%, transparent);
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  box-shadow: 0 6px 22px -16px color-mix(in oklab, var(--rlhf-ink) 55%, transparent);
}
rlhf-session-bar .rlhf-bar-label { font-weight: 600; font-size: 0.9rem; }
rlhf-session-bar .rlhf-progress { display: flex; align-items: center; gap: 0.5rem; }
rlhf-session-bar .rlhf-progress-track {
  width: 7rem; height: 0.4rem; border-radius: 999px; overflow: hidden;
  background: var(--rlhf-sunk);
}
rlhf-session-bar .rlhf-progress-fill {
  display: block; height: 100%; border-radius: 999px;
  background: var(--rlhf-accent); transition: width 0.3s ease;
}
rlhf-session-bar .rlhf-progress-text {
  font-variant-numeric: tabular-nums; font-size: 0.8rem; color: var(--rlhf-ink-soft);
}
rlhf-session-bar .rlhf-counts { display: flex; flex-wrap: wrap; gap: 0.35rem; }
rlhf-session-bar .rlhf-chip-count {
  font-size: 0.72rem; font-variant-numeric: tabular-nums;
  padding: 0.1rem 0.5rem; border-radius: 999px;
  border: 1px solid var(--rlhf-line); color: var(--rlhf-ink-soft);
}
rlhf-session-bar .rlhf-closed {
  font-size: 0.72rem; font-weight: 600; color: var(--rlhf-positive);
  padding: 0.1rem 0.5rem; border-radius: 999px;
  background: color-mix(in oklab, var(--rlhf-positive) 14%, transparent);
}
rlhf-session-bar .rlhf-bar-actions { margin-left: auto; display: flex; gap: 0.5rem; }
@media (prefers-reduced-motion: reduce) { rlhf-session-bar .rlhf-progress-fill { transition: none; } }
`

const INSTALLED = new WeakSet<Document>()

/** Install the RLHF styles into `doc` once (adopted sheet, `<style>` fallback). */
export function installRlhfStyles(doc: Document = document): void {
  if (INSTALLED.has(doc)) return
  INSTALLED.add(doc)
  const ctorOk =
    typeof CSSStyleSheet !== "undefined" &&
    "adoptedStyleSheets" in doc &&
    typeof CSSStyleSheet.prototype.replaceSync === "function"
  if (ctorOk) {
    try {
      const sheet = new CSSStyleSheet()
      sheet.replaceSync(RLHF_STYLES_CSS)
      doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet]
      return
    } catch {
      /* fall through to the <style> tag */
    }
  }
  if (doc.querySelector("style[data-rlhf]")) return
  const style = doc.createElement("style")
  style.setAttribute("data-rlhf", "")
  style.textContent = RLHF_STYLES_CSS
  doc.head.append(style)
}
